/** Resposta bruta do user.search / user.get (REST Bitrix24). */
export type BitrixUserRow = {
	ID: string | number;
	NAME?: string;
	LAST_NAME?: string;
	EMAIL?: string;
	LOGIN?: string;
};

function normalizeUserList(data: unknown): BitrixUserRow[] {
	if (Array.isArray(data)) {
		return data as BitrixUserRow[];
	}
	if (data && typeof data === 'object') {
		return Object.values(data as Record<string, BitrixUserRow>);
	}
	return [];
}

export function formatBitrixUserLabel(u: BitrixUserRow): string {
	const name = [u.NAME, u.LAST_NAME].filter(Boolean).join(' ').trim();
	if (name) return name;
	if (u.EMAIL) return String(u.EMAIL);
	if (u.LOGIN) return String(u.LOGIN);
	return String(u.ID);
}

const USER_GET_PAGE = 50;
/** Limite de páginas (50 users cada) para não bloquear a UI em portais enormes */
const USER_GET_MAX_PAGES = 40;

/**
 * Lista usuários ativos com paginação (para preencher o select antes de pesquisar).
 * @see https://apidocs.bitrix24.com/api-reference/user/user-get.html
 */
export function bitrixUserListForSelect(): Promise<BitrixUserRow[]> {
	if (typeof window === 'undefined' || !window.BX24) {
		return Promise.resolve([]);
	}

	return new Promise((resolve) => {
		const all: BitrixUserRow[] = [];
		let start = 0;
		let page = 0;

		const fetchPage = () => {
			window.BX24!.callMethod(
				'user.get',
				{
					SORT: 'LAST_NAME',
					ORDER: 'ASC',
					FILTER: { ACTIVE: true },
					start,
				},
				(result: { error: () => unknown; data: () => unknown }) => {
					if (result.error()) {
						resolve(all);
						return;
					}
					const list = normalizeUserList(result.data());
					all.push(...list);
					page += 1;
					if (list.length < USER_GET_PAGE || page >= USER_GET_MAX_PAGES) {
						resolve(all);
						return;
					}
					start += USER_GET_PAGE;
					fetchPage();
				},
			);
		};

		fetchPage();
	});
}

/**
 * Pesquisa acelerada quando há texto (ex.: nome).
 * @see https://apidocs.bitrix24.com/api-reference/user/user-search.html
 */
export function bitrixUserSearch(find: string): Promise<BitrixUserRow[]> {
	const q = (find || '').trim();
	if (!q || typeof window === 'undefined' || !window.BX24) {
		return Promise.resolve([]);
	}

	return new Promise((resolve) => {
		window.BX24!.callMethod(
			'user.search',
			{
				FILTER: { FIND: q },
				SORT: 'LAST_NAME',
				ORDER: 'ASC',
				start: 0,
			},
			(result: { error: () => unknown; data: () => unknown }) => {
				if (result.error()) {
					resolve([]);
					return;
				}
				resolve(normalizeUserList(result.data()));
			},
		);
	});
}

export function bitrixUserGetById(id: string): Promise<BitrixUserRow | null> {
	const cleanId = (id || '').trim();
	if (!cleanId || typeof window === 'undefined' || !window.BX24) {
		return Promise.resolve(null);
	}

	return new Promise((resolve) => {
		window.BX24!.callMethod(
			'user.get',
			{
				FILTER: { ID: cleanId },
			},
			(result: { error: () => unknown; data: () => unknown }) => {
				if (result.error()) {
					resolve(null);
					return;
				}
				const list = normalizeUserList(result.data());
				resolve(list[0] ?? null);
			},
		);
	});
}
